import React, { useState } from 'react';
import { View, Text, StyleSheet, Platform, LayoutChangeEvent } from 'react-native';
import { Brand, Colors, Radii, Shadows, Spacing, Typography, LAYOUT } from '../constants';

/**
 * Every band is the same height, whatever it holds (kicker or not, display
 * or title, toolbar or not), so the headline sits on one line across pages
 * and the white column starts at the same place: no jump between screens.
 * The title block is anchored `TITLE_BOTTOM` above the band's lower edge;
 * a toolbar hangs off that edge (half on green, half on the column) and
 * takes no height of its own.
 */
export const BAND_HEIGHT = 156;
const TITLE_BOTTOM = 52; // toolbar's upper half (38) + 14 gap

interface PageBandProps {
  /** Page headline — a plain string, or a node when parts need their own ink (the italic first name). */
  title: React.ReactNode;
  /** Overline above the title: saliemist on the green band (the count, the club names). */
  kicker?: string;
  /** Right-hand slot on the title row — usually `StatsBadge onDark`. */
  right?: React.ReactNode;
  /** White panel that straddles the band's lower edge: search · filter · one button. */
  toolbar?: React.ReactNode;
  /** Home only: the greeting is `Typography.display` (44) instead of `Typography.title` (36). */
  display?: boolean;
  testID?: string;
}

/**
 * Desktop page band (design v2): the navbar's Bosgroen continues under the
 * page headline. The inner content is centred at `LAYOUT.maxContentWidth`,
 * and an optional white toolbar panel straddles the band's lower edge — half
 * on green, half on the white column below. Give that column `paddingTop` 76
 * (`ScreenLayout band`) so the lower half has room.
 *
 * Desktop only: mobile screens render their own white header.
 */
export const PageBand: React.FC<PageBandProps> = ({
  title,
  kicker,
  right,
  toolbar,
  display = false,
  testID,
}) => {
  // The toolbar is pulled down by half its own height. RN web takes a
  // percentage translate; native needs the measured height.
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const onToolbarLayout = (e: LayoutChangeEvent) => setToolbarHeight(e.nativeEvent.layout.height);
  const translateY = Platform.OS === 'web' ? '50%' : toolbarHeight / 2;

  return (
    <View style={styles.band} testID={testID}>
      <View style={styles.inner}>
        <View style={styles.titleRow}>
          <View style={styles.titleBlock}>
            {kicker ? <Text style={styles.kicker}>{kicker}</Text> : null}
            {typeof title === 'string' ? (
              <Text style={[display ? Typography.display : Typography.title, styles.title]}>{title}</Text>
            ) : (
              title
            )}
          </View>
          {right}
        </View>

        {toolbar ? (
          <View
            style={[styles.toolbar, { transform: [{ translateY }] as any }]}
            onLayout={onToolbarLayout}
          >
            {toolbar}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  band: {
    width: '100%',
    backgroundColor: Brand.bosgroen,
    alignItems: 'center',
  },
  inner: {
    width: '100%',
    maxWidth: LAYOUT.maxContentWidth,
    height: BAND_HEIGHT,
    justifyContent: 'flex-end',
    paddingHorizontal: Spacing['3xl'],
    paddingBottom: TITLE_BOTTOM,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: Spacing.xl,
  },
  titleBlock: {
    flexShrink: 1,
    minWidth: 0,
  },
  kicker: {
    ...Typography.overline,
    color: Brand.saliemist,
    marginBottom: 10,
  },
  title: {
    color: Colors.white,
  },
  toolbar: {
    // Hangs off the band's lower edge; sits above the white column so its
    // lower half stays visible.
    position: 'absolute',
    left: Spacing['3xl'],
    right: Spacing['3xl'],
    bottom: 0,
    zIndex: 2,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.panel,
    padding: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    ...Shadows.cardHover,
  },
});
